import React, { useState } from 'react';
import { Package, Truck, DollarSign, Activity, ChevronRight } from 'lucide-react';
import PhotoCapture from './PhotoCapture';
import TrustBadge from './TrustBadge';

const StorekeeperView = ({ data, save, user, addNotification }) => {
  const [view, setView] = useState('home');
  const [form, setForm] = useState({});
  const [photos, setPhotos] = useState({});

  const createBatch = () => {
    if (!photos.doc || !photos.items || !form.name || !form.qty) {
      alert('Missing required fields or photos');
      return;
    }
    if (parseInt(form.qty) <= 0) {
      alert('Quantity must be greater than 0');
      return;
    }
    const batch = {
      id: `BAT-${Date.now()}`,
      productName: form.name,
      quantity: parseInt(form.qty),
      supplier: form.supplier || 'Unknown',
      unitCost: parseFloat(form.cost || 0),
      photos,
      createdBy: user.name,
      createdAt: new Date().toISOString(),
      status: 'in_storage',
      custody: 'company'
    };
    save('batches', [...data.batches, batch]);
    addNotification('Batch Created', `${form.name} — ${form.qty} units added`, 'success');
    alert('✅ Batch created');
    setView('home');
    setForm({});
    setPhotos({});
  };

  const prepareDispatch = (batch) => {
    if (!photos.packed || !photos.sealed || !form.qty) {
      alert('Missing required fields or photos');
      return;
    }
    const qty = parseInt(form.qty);
    if (qty <= 0 || qty > batch.quantity) {
      alert(`Quantity must be between 1 and ${batch.quantity}`);
      return;
    }
    const dispatch = {
      id: `DSP-${Date.now()}`,
      batchId: batch.id,
      productName: batch.productName,
      quantity: qty,
      photos,
      preparedBy: user.name,
      preparedAt: new Date().toISOString(),
      status: 'pending_approval'
    };
    save('dispatches', [...data.dispatches, dispatch]);
    save('batches', data.batches.map(b =>
      b.id === batch.id ? { ...b, status: 'dispatch_prepared' } : b
    ));
    addNotification('Dispatch Prepared', `${batch.productName} ready for approval`, 'info');
    alert('✅ Dispatch prepared');
    setView('home');
    setForm({});
    setPhotos({});
  };

  if (view === 'intake') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">New Intake</h2>
          <button onClick={() => { setView('home'); setForm({}); setPhotos({}); }} className="text-2xl text-gray-500">×</button>
        </div>
        <PhotoCapture label="Supplier document *" onCapture={ph => setPhotos({...photos, doc: ph})} />
        <input type="text" placeholder="Product name *" className="w-full p-3 border-2 rounded-lg" value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})} />
        <PhotoCapture label="Photo of items *" onCapture={ph => setPhotos({...photos, items: ph})} />
        <div className="grid grid-cols-2 gap-3">
          <input type="number" placeholder="Quantity *" className="w-full p-3 border-2 rounded-lg" value={form.qty || ''} onChange={e => setForm({...form, qty: e.target.value})} />
          <input type="number" placeholder="Unit cost (P)" step="0.01" className="w-full p-3 border-2 rounded-lg" value={form.cost || ''} onChange={e => setForm({...form, cost: e.target.value})} />
        </div>
        <input type="text" placeholder="Supplier name" className="w-full p-3 border-2 rounded-lg" value={form.supplier || ''} onChange={e => setForm({...form, supplier: e.target.value})} />
        <div className="flex gap-3">
          <button onClick={() => { setView('home'); setForm({}); setPhotos({}); }} className="flex-1 bg-gray-200 p-3 rounded-lg font-semibold">Cancel</button>
          <button onClick={createBatch} className="flex-1 bg-blue-600 text-white p-3 rounded-lg font-bold">Save Batch</button>
        </div>
      </div>
    );
  }

  if (view === 'dispatch') {
    const storage = data.batches.filter(b => b.status === 'in_storage');
    if (form.batchId) {
      const batch = data.batches.find(b => b.id === form.batchId);
      return (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Prepare Dispatch</h2>
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <p className="font-bold text-lg">{batch.productName}</p>
            <p className="text-sm text-gray-600">Available: {batch.quantity} units</p>
            <p className="text-sm text-gray-600">Value: P {(batch.quantity * batch.unitCost).toFixed(2)}</p>
          </div>
          <input type="number" placeholder="Qty to dispatch *" className="w-full p-3 border-2 rounded-lg text-lg" value={form.qty || ''} onChange={e => setForm({...form, qty: e.target.value})} />
          <PhotoCapture label="Packed items *" onCapture={ph => setPhotos({...photos, packed: ph})} />
          <PhotoCapture label="Sealed packages *" onCapture={ph => setPhotos({...photos, sealed: ph})} />
          <div className="flex gap-3">
            <button onClick={() => setForm({})} className="flex-1 bg-gray-200 p-3 rounded-lg font-semibold">Back</button>
            <button onClick={() => prepareDispatch(batch)} className="flex-1 bg-green-600 text-white p-3 rounded-lg font-bold">Submit</button>
          </div>
        </div>
      );
    }
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold">Select Batch to Dispatch</h2>
        {storage.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No batches in storage</p>
          </div>
        ) : (
          storage.map(b => (
            <div key={b.id} onClick={() => setForm({batchId: b.id})} className="border-2 p-4 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all">
              <p className="font-bold text-lg">{b.productName}</p>
              <p className="text-sm text-gray-600">Qty: {b.quantity} • P {(b.quantity * b.unitCost).toFixed(0)}</p>
              <p className="text-xs text-gray-400 mt-1">{b.id}</p>
            </div>
          ))
        )}
        <button onClick={() => setView('home')} className="w-full bg-gray-200 p-3 rounded-lg font-semibold">Cancel</button>
      </div>
    );
  }

  const inStorage = data.batches.filter(b => b.status === 'in_storage').length;
  const totalVal = data.batches.reduce((s, b) => s + (b.quantity * b.unitCost), 0);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold">Storekeeper</h2>
          <TrustBadge score={user.trustScore} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <Package className="w-8 h-8 text-blue-600 mb-1" />
          <p className="text-2xl font-bold">{inStorage}</p>
          <p className="text-sm text-gray-600">In Storage</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <DollarSign className="w-8 h-8 text-green-600 mb-1" />
          <p className="text-2xl font-bold">P {totalVal.toFixed(0)}</p>
          <p className="text-sm text-gray-600">Total Value</p>
        </div>
      </div>
      <button onClick={() => setView('intake')} className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white p-5 rounded-xl flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-3">
          <Package className="w-6 h-6" />
          <span className="font-bold text-lg">New Intake</span>
        </div>
        <ChevronRight className="w-6 h-6" />
      </button>
      <button onClick={() => setView('dispatch')} className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white p-5 rounded-xl flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-3">
          <Truck className="w-6 h-6" />
          <span className="font-bold text-lg">Prepare Dispatch</span>
        </div>
        <ChevronRight className="w-6 h-6" />
      </button>
      <div>
        <h3 className="font-bold mb-2 flex items-center gap-2">
          <Activity className="w-4 h-4" />
          Recent Batches
        </h3>
        {data.batches.length === 0 ? (
          <p className="text-gray-400 text-center py-6">No batches yet</p>
        ) : (
          data.batches.slice(-5).reverse().map(b => (
            <div key={b.id} className="border p-3 rounded-lg mb-2 hover:bg-gray-50">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-semibold">{b.productName}</p>
                  <p className="text-xs text-gray-500">{b.id}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                  b.status === 'in_storage' ? 'bg-blue-100 text-blue-700' :
                  b.status === 'dispatch_prepared' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-gray-100 text-gray-600'
                }`}>{b.status}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default StorekeeperView;
